import { useEffect, useRef, useState } from "react";
import type { PublicAppointmentHold } from "../../../services/public";
import { getPublicAvailability } from "../../../services/public";
import { getCustomerMe } from "../../../services/customerAuth";
import {
  checkPublicCustomerMembershipCoverage,
  checkPublicCustomerMembershipNotice,
} from "../../../services/customerMemberships";
import { shouldClearCustomerSession } from "../utils";
import {
  resolveCustomerCoveragePromptMessage,
  resolveServiceCoverageReasonLabel,
} from "../utils/coveragePrompt";
import type { BookingDecisionSummary } from "../components/BookingDecisionDialog";

export type DecisionPayload = {
  name: string;
  email: string;
  whatsapp?: string;
  phone?: string;
  notes?: string;
  date: string;
  time: string;
  serviceId?: string;
  serviceIds?: string[];
  providerId?: string;
  customFields?: Record<string, unknown>;
};
type Customer = Awaited<ReturnType<typeof getCustomerMe>>;
type Coverage = Awaited<ReturnType<typeof checkPublicCustomerMembershipCoverage>>;
export type BookingDecisionContext = {
  selectionKey: string;
  coverage?: Coverage;
  resume?: { hold: PublicAppointmentHold; quotedAmountCents: number };
};
export type BookingDecisionSubmitResult =
  | boolean
  | {
      confirmation: BookingDecisionSummary;
      resume: NonNullable<BookingDecisionContext["resume"]>;
    };
type Options = {
  slug: string | null;
  selectionKey: string;
  contactKey?: string;
  token: string | null;
  sessionLoading: boolean;
  services: Array<{ id: string; name: string }>;
  onCustomer: (customer: Customer, token: string) => void;
  onExpired: (token: string) => void;
  onUnavailable: () => void;
  submit: (
    payload: DecisionPayload,
    token: string | null,
    isCurrent: () => boolean,
    context?: BookingDecisionContext
  ) => Promise<BookingDecisionSubmitResult>;
};
type Pending = {
  payload: DecisionPayload;
  token: string | null;
  coverage?: string;
  context?: BookingDecisionContext;
  hydrationPayload?: DecisionPayload;
  guestDecisionRequired?: boolean;
};
function contactMatches(key: string | undefined, payload: DecisionPayload) {
  if (key === undefined) return true;
  try {
    const values = JSON.parse(key);
    return (
      Array.isArray(values) &&
      String(values[0] || "")
        .trim()
        .toLowerCase() === payload.email.trim().toLowerCase() &&
      String(values[1] || "").replace(/\D/g, "") ===
        String(payload.whatsapp || "").replace(/\D/g, "") &&
      String(values[2] || "").replace(/\D/g, "") ===
        String(payload.phone || "").replace(/\D/g, "")
    );
  } catch {
    return false;
  }
}
const money = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const expired = () =>
  Object.assign(
    new Error(
      "Sua sessão expirou. Entre novamente para verificar a assinatura ou escolha continuar como visitante."
    ),
    { status: 401 }
  );

export function useBookingDecision(options: Options) {
  const latest = useRef(options);
  latest.current = options;
  const generation = useRef(0);
  const locked = useRef(false);
  const lockOwner = useRef<number | null>(null);
  const mutating = useRef(false);
  const complete = useRef(false);
  const pending = useRef<Pending | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [summary, setSummary] = useState<BookingDecisionSummary | null>(null);
  const [canChooseGuest, setCanChooseGuest] = useState(false);
  const [hasMembershipMatch, setHasMembershipMatch] = useState(false);

  useEffect(() => {
    generation.current += 1;
    pending.current = null;
    complete.current = false;
    locked.current = false;
    lockOwner.current = null;
    setBusy(false);
    setOpen(false);
    setSummary(null);
    setError(null);
  }, [options.selectionKey]);
  useEffect(() => {
    // A hidratação da identidade após login não invalida a própria sessão.
    if (
      pending.current &&
      (!pending.current.token ||
        !contactMatches(
          options.contactKey,
          pending.current.hydrationPayload || pending.current.payload
        ))
    ) {
      generation.current += 1;
      pending.current = null;
      locked.current = false;
      lockOwner.current = null;
      setBusy(false);
      setOpen(false);
      setSummary(null);
      setError(null);
      setNotice(null);
      setCanChooseGuest(false);
    }
  }, [options.contactKey, options.slug]);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    []
  );
  useEffect(() => {
    // A sessão instalada pelo login corresponde ao token pendente. Qualquer
    // outra mudança (inclusive logout) invalida a decisão da conta anterior.
    if (pending.current && pending.current.token !== options.token) {
      generation.current += 1;
      pending.current = null;
      locked.current = false;
      lockOwner.current = null;
      setBusy(false);
      setOpen(false);
      setSummary(null);
    }
  }, [options.token]);

  const run = async (work: (valid: () => boolean) => Promise<void>) => {
    if (
      locked.current ||
      mutating.current ||
      complete.current ||
      latest.current.sessionLoading
    )
      return;
    locked.current = true;
    setBusy(true);
    setError(null);
    const attempt = ++generation.current;
    lockOwner.current = attempt;
    const key = latest.current.selectionKey;
    const contactKey = latest.current.contactKey;
    const slug = latest.current.slug;
    const valid = () =>
      attempt === generation.current &&
      key === latest.current.selectionKey &&
      slug === latest.current.slug &&
      (contactKey === latest.current.contactKey ||
        Boolean(
          pending.current?.token &&
          contactMatches(
            latest.current.contactKey,
            pending.current.hydrationPayload || pending.current.payload
          )
        ));
    try {
      await work(valid);
    } catch (failure) {
      if (!valid()) return;
      if (shouldClearCustomerSession(failure) && pending.current?.token) {
        latest.current.onExpired(pending.current.token);
        pending.current = {
          ...pending.current,
          token: null,
          coverage: undefined,
          context: undefined,
          guestDecisionRequired: true,
        };
        setSummary(null);
        setCanChooseGuest(true);
        setHasMembershipMatch(false);
        setNotice(expired().message);
        setError(null);
        setOpen(true);
        return;
      }
      setOpen(true);
      setError(
        shouldClearCustomerSession(failure)
          ? expired().message
          : failure instanceof Error
            ? failure.message
            : "Não foi possível verificar seu agendamento. Tente novamente."
      );
    } finally {
      if (lockOwner.current === attempt) {
        locked.current = false;
        lockOwner.current = null;
        setBusy(false);
      }
    }
  };

  const checkSlot = async (payload: DecisionPayload, valid: () => boolean) => {
    const result = await getPublicAvailability(
      latest.current.slug!,
      payload.date,
      payload.serviceId,
      payload.providerId,
      payload.serviceIds
    );
    if (!valid()) return false;
    if (![...(result.slots || []), ...(result.fitSlots || [])].includes(payload.time)) {
      pending.current = null;
      setOpen(false);
      latest.current.onUnavailable();
      return false;
    }
    return true;
  };

  const checkAccount = async (entry: Pending, valid: () => boolean) => {
    const token = entry.token!;
    const previousSession = latest.current.token;
    const account = await getCustomerMe(token);
    if (!valid()) return null;
    if (!account)
      throw new Error(
        "Não foi possível verificar sua conta. Tente novamente antes de confirmar."
      );
    if (latest.current.token !== previousSession) return null;
    const payload = {
      ...entry.payload,
      name: account.fullName?.trim() || entry.payload.name,
      email: account.email?.trim().toLowerCase() || entry.payload.email,
      whatsapp: account.whatsapp || account.phone || entry.payload.whatsapp,
      phone: account.phone || entry.payload.phone,
    };
    // Autoriza apenas a hidratação dos contatos comprovados pela própria conta.
    // Uma edição posterior fora desse payload invalida também a tentativa autenticada.
    pending.current = { ...entry, hydrationPayload: payload };
    latest.current.onCustomer(account, token);
    if (!(await checkSlot(payload, valid))) return null;
    const coverage = await checkPublicCustomerMembershipCoverage(
      latest.current.slug!,
      {
        serviceId: payload.serviceId,
        serviceIds: payload.serviceIds,
        providerId: payload.providerId,
        date: payload.date,
        time: payload.time,
      },
      token
    );
    if (!valid()) return null;
    if (!coverage.authenticated || coverage.reason === "AUTH_REQUIRED") throw expired();
    const nextSummary = describeCoverage(
      coverage,
      account.fullName || "cliente",
      latest.current.services,
      payload.date
    );
    return {
      entry: {
        payload,
        token,
        coverage: JSON.stringify(coverageFingerprint(coverage)),
        context: {
          selectionKey: latest.current.selectionKey,
          coverage,
          ...(entry.context?.resume ? { resume: entry.context.resume } : {}),
        },
      },
      summary: nextSummary,
    };
  };

  const mutate = async (entry: Pending, valid: () => boolean) => {
    if (!valid()) return;
    mutating.current = true;
    setSubmitting(true);
    let success: BookingDecisionSubmitResult;
    try {
      success = entry.context
        ? await latest.current.submit(entry.payload, entry.token, valid, entry.context)
        : await latest.current.submit(entry.payload, entry.token, valid);
    } finally {
      mutating.current = false;
      setSubmitting(false);
    }
    if (!valid()) return;
    if (typeof success === "object") {
      pending.current = {
        ...entry,
        context: {
          selectionKey: latest.current.selectionKey,
          ...entry.context,
          resume: success.resume,
        },
      };
      setSummary(success.confirmation);
      setError(null);
      setCanChooseGuest(false);
      setOpen(true);
    } else if (success) {
      complete.current = true;
      pending.current = null;
      setOpen(false);
    } else {
      setError(
        "Não foi possível confirmar o agendamento. Confira os dados e tente novamente."
      );
      setOpen(true);
    }
  };

  const prepare = async (valid: () => boolean) => {
    const entry = pending.current;
    if (!entry || !latest.current.slug) return;
    if (entry.token) {
      setOpen(true);
      const result = await checkAccount(entry, valid);
      if (!result) return;
      pending.current = result.entry;
      setSummary(result.summary);
      setCanChooseGuest(false);
    } else {
      setHasMembershipMatch(false);
      let showNotice = true;
      if (!entry.guestDecisionRequired) {
        setNotice(null);
        try {
          const result = await checkPublicCustomerMembershipNotice(latest.current.slug, {
            email: entry.payload.email,
            phone: entry.payload.whatsapp || entry.payload.phone,
          });
          if (!valid()) return;
          showNotice = result.showNotice;
          setHasMembershipMatch(result.showNotice);
        } catch {
          if (!valid()) return;
          setNotice(
            "Não foi possível verificar uma possível assinatura. Tente novamente ou confirme como visitante pelo valor avulso."
          );
        }
      }
      if (!valid()) return;
      if (!showNotice) {
        if (await checkSlot(entry.payload, valid)) await mutate(entry, valid);
        return;
      }
      setSummary(null);
      setCanChooseGuest(true);
      setOpen(true);
    }
  };

  const begin = (payload: DecisionPayload) =>
    run(async (valid) => {
      if (pending.current || !latest.current.slug) return;
      pending.current = { payload, token: latest.current.token };
      setCanChooseGuest(false);
      setSummary(null);
      setNotice(null);
      setHasMembershipMatch(false);
      setOpen(false);
      await prepare(valid);
    });
  const login = (token: string) =>
    run(async (valid) => {
      if (!pending.current) return;
      pending.current = {
        ...pending.current,
        token,
        coverage: undefined,
        context: undefined,
      };
      const result = await checkAccount(pending.current, valid);
      if (!result) return;
      pending.current = result.entry;
      setSummary(result.summary);
      setCanChooseGuest(false);
    });
  const confirm = () =>
    run(async (valid) => {
      const entry = pending.current;
      if (!entry) return;
      if (entry.context?.resume) {
        const { hold } = entry.context.resume;
        if (
          entry.context.selectionKey !== latest.current.selectionKey ||
          (hold.expiresAt &&
            (!Number.isFinite(new Date(hold.expiresAt).getTime()) ||
              new Date(hold.expiresAt).getTime() <= Date.now()))
        ) {
          throw new Error("A reserva expirou. Selecione o horário novamente.");
        }
        // O próprio hold ocupa o horário e a cota. Revalidar a sessão, sem
        // consultar disponibilidade/cobertura como se fosse uma nova reserva.
        if (entry.token) {
          const account = await getCustomerMe(entry.token);
          if (!valid()) return;
          if (!account) throw expired();
        }
        if (valid()) await mutate(entry, valid);
        return;
      }
      if (entry.token) {
        const result = await checkAccount(entry, valid);
        if (!result) return;
        pending.current = result.entry;
        setSummary(result.summary);
        if (entry.coverage !== result.entry.coverage) return; // novo valor exige novo aceite
        await mutate(result.entry, valid);
      } else if (
        (canChooseGuest || entry.context?.resume) &&
        (await checkSlot(entry.payload, valid))
      ) {
        await mutate(entry, valid);
      }
    });
  const close = () => {
    if (mutating.current) return;
    generation.current += 1;
    locked.current = false;
    lockOwner.current = null;
    setBusy(false);
    pending.current = null;
    setOpen(false);
    setSummary(null);
    setError(null);
    setNotice(null);
    setHasMembershipMatch(false);
  };
  return {
    open,
    busy,
    submitting,
    error,
    notice,
    summary,
    canChooseGuest,
    hasMembershipMatch,
    begin,
    login,
    confirm,
    close,
    retry: () => run(prepare),
  };
}

function coverageFingerprint(coverage: Coverage) {
  return {
    covered: coverage.covered,
    partial: coverage.partial,
    reason: coverage.reason,
    amountDueCents: coverage.amountDueCents,
    serviceCoverages: coverage.serviceCoverages,
  };
}

function describeCoverage(
  coverage: Coverage,
  name: string,
  services: Options["services"],
  date: string
): BookingDecisionSummary {
  const covered = coverage.covered;
  const amount = coverage.amountDueCents;
  return {
    name,
    membershipPlanName:
      coverage.subscription?.planId && coverage.plan?.id === coverage.subscription.planId
        ? coverage.plan.name.trim() || undefined
        : undefined,
    message: covered
      ? "Os serviços deste agendamento estão cobertos pela sua assinatura."
      : coverage.partial
        ? "Sua assinatura cobre parte do pedido. Você paga só os serviços que ficam de fora."
        : coverage.subscription
          ? resolveCustomerCoveragePromptMessage({
              reason: coverage.reason,
              fallbackMessage: coverage.message,
              scheduledWeekdayLabel: coverage.coverage?.scheduledWeekdayLabel,
              coveredWeekdayLabels: coverage.coverage?.coveredWeekdayLabels,
              currentPeriodEnd: coverage.subscription.currentPeriodEnd,
              selectedDate: date,
            })
          : "Não encontramos assinatura válida para este atendimento. Confira o valor avulso antes de confirmar.",
    amountLabel: covered
      ? "Serviços cobertos pela assinatura"
      : typeof amount === "number" && Number.isFinite(amount)
        ? `Total de serviços a pagar: ${money(amount)}`
        : "Valor dos serviços a confirmar no atendimento.",
    confirmLabel:
      !covered && typeof amount === "number" && amount > 0
        ? `Continuar e pagar ${money(amount)}`
        : "Confirmar agendamento",
    adjustLabel: !covered
      ? coverage.reason === "WEEKDAY_NOT_INCLUDED"
        ? "Escolher outro dia"
        : "Ajustar atendimento"
      : undefined,
    services: (coverage.serviceCoverages || []).map((item) => ({
      id: item.serviceId,
      name: services.find((service) => service.id === item.serviceId)?.name || "Serviço",
      label: item.coveredByMembership
        ? "Coberto pela assinatura"
        : `Fora da assinatura: ${resolveServiceCoverageReasonLabel(item.membershipCoverageReason)}`,
      amountLabel:
        !item.coveredByMembership && Number.isFinite(item.amountCents)
          ? money(item.amountCents)
          : undefined,
    })),
  };
}
